<template>
  <section class="contact-editor">
    <header>
      <div>
        <h4>Company & contact</h4>
        <p>Account details and the person to contact.</p>
      </div>
      <b-button v-if="!editing" variant="outline-secondary" @click="begin">Edit details</b-button>
    </header>
    <div v-if="!editing" class="contact-summary">
      <section aria-label="Company details">
        <h5>Company details</h5>
        <dl class="contact-facts">
          <div
            v-for="field in fields.filter((item) =>
              ['name', 'clientNumber', 'shortName', 'contractParty'].includes(item.key),
            )"
            :key="field.key"
          >
            <dt>{{ field.label }}</dt>
            <dd>{{ company[field.key] || '—' }}</dd>
          </div>
          <div>
            <dt>Assigned salesman</dt>
            <dd>{{ salesmanName }}</dd>
          </div>
        </dl>
      </section>
      <section aria-label="Contact person">
        <h5>Contact person</h5>
        <dl class="contact-facts">
          <div>
            <dt>Name</dt>
            <dd>{{ [company.contactName, company.contactLastName].filter(Boolean).join(' ') || '—' }}</dd>
          </div>
          <div>
            <dt>Email address</dt>
            <dd>{{ company.email || '—' }}</dd>
          </div>
          <div>
            <dt>Phone number</dt>
            <dd>{{ company.phoneNumber || '—' }}</dd>
          </div>
        </dl>
      </section>
    </div>
    <form v-else @submit.prevent="save">
      <fieldset :disabled="saving">
        <legend class="visually-hidden">Company and contact details</legend>
        <div class="contact-grid">
          <div v-for="field in fields" :key="field.key">
            <label :for="`${company.id}-${field.key}`">{{ field.label }}</label><input
              :id="`${company.id}-${field.key}`"
              v-model="draft[field.key]"
              :type="field.type || 'text'"
              :required="field.key === 'email'"
              class="form-control"
            />
          </div>
          <div>
            <label :for="`${company.id}-salesman`">Assigned salesman</label><select :id="`${company.id}-salesman`" v-model="salesmanId" class="form-control">
              <option value="" :disabled="!!company.salesmanId">Not assigned</option>
              <option
                v-if="company.salesmanId && !staffs.some((staff) => staff.id === company.salesmanId)"
                :value="company.salesmanId"
              >
                {{ salesmanName }}
              </option>
              <option v-for="staff in staffs" :key="staff.id || staff.email" :value="staff.id">
                {{ staff.firstName ? `${staff.firstName} ${staff.lastName || ''}` : staff.email }}
              </option>
            </select>
          </div>
        </div>
      </fieldset>
      <p v-if="error" class="text-danger mt-3" role="alert">{{ error }}</p>
      <div class="contact-actions">
        <span>Changes apply only to these details.</span><b-button class="btn-grey" :disabled="saving" @click="cancel">Cancel</b-button><b-button type="submit" class="btn-green" :disabled="saving || !changed">
          {{ saving ? 'Saving…' : 'Save details' }}
        </b-button>
      </div>
    </form>
    <p v-if="saved && !editing" role="status" class="text-success mt-3 mb-0">Company details saved.</p>
  </section>
</template>

<script lang="ts">
import type { CustomerContactEditorProps, CustomerInlineField } from '@/types/Components/Customer'
import { BButton } from 'bootstrap-vue-next'
import { computed, ref, defineComponent, type Ref, type ComputedRef, type PropType } from 'vue'
import { useCustomersStore } from '@/store/customers'
import { usePackingSlipStore } from '@/store/packingSlip'
import CompanyProxy from '@/proxies/CompanyProxy'

export default defineComponent({
  name: 'CustomerContactEditor',
  components: { BButton },
  props: {
    company: { type: Object as PropType<CustomerContactEditorProps['company']>, required: true },
  },
  setup(props) {
    const customers = useCustomersStore()
    const packingSlip = usePackingSlipStore()
    const fields: CustomerInlineField[] = [
      { key: 'name', label: 'Company' },
      { key: 'clientNumber', label: 'Client №' },
      { key: 'shortName', label: 'Short name' },
      { key: 'contractParty', label: 'Contract party' },
      { key: 'contactName', label: 'First name' },
      { key: 'contactLastName', label: 'Last name' },
      { key: 'email', label: 'Email address', type: 'email' },
      { key: 'phoneNumber', label: 'Phone number', type: 'tel' },
    ]
    const editing: Ref<boolean> = ref(false)
    const saving: Ref<boolean> = ref(false)
    const saved: Ref<boolean> = ref(false)
    const error: Ref<string> = ref('')
    const draft: Ref<Record<string, string>> = ref<Record<string, string>>({})
    const initial: Ref<Record<string, string>> = ref<Record<string, string>>({})
    const salesmanId: Ref<string> = ref('')
    const staffs: ComputedRef<ReturnType<typeof usePackingSlipStore>['staffs']> = computed(
      () => packingSlip.staffs || [],
    )
    const salesmanName: ComputedRef<string> = computed(() => {
      const salesman = props.company.salesman
      return salesman
        ? [salesman.first_name, salesman.last_name].filter(Boolean).join(' ') || salesman.email || 'Not assigned'
        : 'Not assigned'
    })
    const changed: ComputedRef<boolean> = computed(
      () =>
        fields.some((field) => draft.value[field.key] !== initial.value[field.key]) ||
        salesmanId.value !== (props.company.salesmanId || ''),
    )
    function begin(): void {
      initial.value = Object.fromEntries(fields.map((field) => [field.key, props.company[field.key] || '']))
      draft.value = { ...initial.value }
      salesmanId.value = props.company.salesmanId || ''
      editing.value = true
      saved.value = false
      error.value = ''
      if (!staffs.value.length) packingSlip.fetchStaffs()
    }
    function cancel(): void {
      editing.value = false
      error.value = ''
    }
    function save(): void {
      if (saving.value || !changed.value) return
      saving.value = true
      error.value = ''
      const data: Record<string, string> = Object.fromEntries(
        fields
          .filter((field) => draft.value[field.key] !== initial.value[field.key])
          .map((field) => [field.key, draft.value[field.key]]),
      )
      if (salesmanId.value !== (props.company.salesmanId || '')) data.assignedSalesman = salesmanId.value
      new CompanyProxy()
        .update(props.company.id, data)
        .then(() => {
          const customer = customers.listCustomers?.find((item) => item.id === props.company.id)
          if (customer) {
            const { assignedSalesman, ...details } = data
            Object.assign(customer, details)
            if ('shortName' in details) customer.shortName = details.shortName.toUpperCase()
            if (assignedSalesman) {
              const staff = staffs.value.find((item) => item.id === assignedSalesman)
              customer.salesmanId = assignedSalesman
              if (staff)
                customer.salesman = { first_name: staff.firstName, last_name: staff.lastName, email: staff.email }
            }
          }
          editing.value = false
          saved.value = true
        })
        .catch(() => {
          error.value = 'Could not save these details. Check the values and try again. Your changes are still here.'
        })
        .finally(() => {
          saving.value = false
        })
    }
    return {
      customers,
      packingSlip,
      fields,
      editing,
      saving,
      saved,
      error,
      draft,
      initial,
      salesmanId,
      staffs,
      salesmanName,
      changed,
      begin,
      cancel,
      save,
    }
  },
})
</script>
